/**
 * Client-side derivations over teamform-api's getAggregations (raw counts
 * only, no ratios/percentages — see teamAggregations.ts). This is the "small
 * new file" the OD/Capabilities Insights migration needs: wherever the old
 * getInsights-backed teamMetrics.ts returned a computed value ready-made
 * (management overhead %, manager∶IC ratio, ...), we now derive it here from
 * TeamAggregations.
 *
 * Both lenses (supply/team-owners and reporting_line/people-managers) are
 * exact DB aggregates now — select which one a calculator sees with
 * selectLensView() before calling it (memberCount/vacancyCount/agentCount
 * are lens-independent population counts, so they pass through unchanged).
 */
import {
  getRAGStatus,
  type RagStatus,
  type SpanRange,
} from "src/util/teamMetrics";
import {
  DEFAULT_VALUES,
  getScenarioTargets,
  type ScenarioTargetOverrides,
} from "src/components/shared/TeamMetrics/constants";
import {
  formatPercent2,
  formatRatio2,
  formatSignedDecimal2,
} from "src/util/metricFormats";
import {
  isReportingLineHierarchySource,
  isSupplyHierarchySource,
} from "src/util/insightsHierarchyView";
import type { TeamAggregations } from "src/util/teamAggregations";

/**
 * Picks the supply- or reporting_line-named fields off TeamAggregations and
 * returns them under the lens-agnostic field names the calculators below
 * expect. memberCount/vacancyCount/agentCount describe the population, not
 * the manager relationship, so they're the same for either lens.
 *
 * Unknown / not-yet-ready lens → {} so callers don't paint supply fields
 * while the workspace default (e.g. reporting_line) is still resolving.
 */
export const selectLensView = (
  aggregations: TeamAggregations,
  hierarchyView?: string | null
): TeamAggregations => {
  if (isReportingLineHierarchySource(hierarchyView)) {
    return {
      memberCount: aggregations.memberCount,
      // FTE is a design-capacity field (from supply memberships), not a
      // reporting-line field — pass it through lens-independently so that
      // callers that consume lensAggregations.memberFteSum keep the value
      // regardless of which lens the user has selected.
      memberFteSum: aggregations.memberFteSum,
      vacancyCount: aggregations.vacancyCount,
      agentCount: aggregations.agentCount,
      positionCount: aggregations.positionCount,
      positionFteSum: aggregations.positionFteSum,
      positionDirectReportsMin: aggregations.positionDirectReportsMin,
      positionDirectReportsMax: aggregations.positionDirectReportsMax,
      positionDirectReportsAvg: aggregations.positionDirectReportsAvg,
      positionDirectReportsChart: aggregations.positionDirectReportsChart,
      positionManagerCount: aggregations.positionManagerCount,
      positionUnassignedCount: aggregations.positionUnassignedCount,
      positionExternalManagers: aggregations.positionExternalManagers,
      positionManagementLayersMin: aggregations.positionManagementLayersMin,
      positionManagementLayersMax: aggregations.positionManagementLayersMax,
      positionManagementLayersAvg: aggregations.positionManagementLayersAvg,
      positionManagementLayersChart: aggregations.positionManagementLayersChart,
      // Occupancy is seat population, not a manager-lens field. Pass it
      // through so Available/Filled chips stay populated on reporting_line.
      positionFilledCount: aggregations.positionFilledCount,
      positionVacantCount: aggregations.positionVacantCount,
      positionFilledFteSum: aggregations.positionFilledFteSum,
      positionVacantFteSum: aggregations.positionVacantFteSum,
      // Caption ownership copy is population metadata, not span/layers.
      externalManagers: aggregations.externalManagers,
      multiTeamOwnerCount: aggregations.multiTeamOwnerCount,
      noDirectOwnerCount: aggregations.noDirectOwnerCount,
      managerCount: aggregations.reportingLineManagerCount,
      unassignedCount: aggregations.reportingLineUnassignedCount,
      directReportsMin: aggregations.reportingLineDirectReportsMin,
      directReportsMax: aggregations.reportingLineDirectReportsMax,
      directReportsAvg: aggregations.reportingLineDirectReportsAvg,
      directReportsChart: aggregations.reportingLineDirectReportsChart,
      managementLayersMin: aggregations.reportingLineManagementLayersMin,
      managementLayersMax: aggregations.reportingLineManagementLayersMax,
      managementLayersAvg: aggregations.reportingLineManagementLayersAvg,
      managementLayersChart: aggregations.reportingLineManagementLayersChart,
    };
  }
  if (isSupplyHierarchySource(hierarchyView)) {
    return aggregations;
  }
  return {};
};

/**
 * Swap the calculator/chip fields onto the funded-seat population.
 * VacancyCount is cleared so Members headcount is seats, not seats + legacy
 * requisitions. Call after selectLensView.
 */
export const selectPositionView = (
  aggregations: TeamAggregations
): TeamAggregations => {
  if (Object.keys(aggregations).length === 0) return aggregations;
  return {
    ...aggregations,
    memberCount: aggregations.positionCount,
    memberFteSum: aggregations.positionFteSum,
    vacancyCount: 0,
    managerCount: aggregations.positionManagerCount,
    unassignedCount: aggregations.positionUnassignedCount,
    externalManagers: aggregations.positionExternalManagers,
    directReportsMin: aggregations.positionDirectReportsMin,
    directReportsMax: aggregations.positionDirectReportsMax,
    directReportsAvg: aggregations.positionDirectReportsAvg,
    directReportsChart: aggregations.positionDirectReportsChart,
    managementLayersMin: aggregations.positionManagementLayersMin,
    managementLayersMax: aggregations.positionManagementLayersMax,
    managementLayersAvg: aggregations.positionManagementLayersAvg,
    managementLayersChart: aggregations.positionManagementLayersChart,
  };
};

/**
 * Design-capacity headcount: allocated people + designed vacant seats.
 * Matches Members help copy / prior memberHeadcount (requisitions & agents
 * are already excluded from these aggregation fields).
 */
export const calculateDesignCapacityHeadcount = (
  aggregations: TeamAggregations
): number | null => {
  if (aggregations.memberCount == null) return null;
  return aggregations.memberCount + (aggregations.vacancyCount ?? 0);
};

/**
 * Vacancy as the difference between the two populations: funded seats minus
 * people actually on the team. Empty-seat occupancy is a separate chip.
 */
export const calculateDesignCapacityGap = (
  positionCount: number | null | undefined,
  actualHeadcount: number | null | undefined
): number | null => {
  if (
    positionCount == null ||
    actualHeadcount == null ||
    Number.isNaN(positionCount) ||
    Number.isNaN(actualHeadcount)
  ) {
    return null;
  }
  return Math.max(0, positionCount - actualHeadcount);
};

const round = (value: number | null | undefined, decimals = 2): number => {
  if (value == null || Number.isNaN(value)) return 0;
  return Number(value.toFixed(decimals));
};

/** Individual contributors = members who have a manager and aren't one themselves. */
export const calculateIndividualContributorCount = (
  aggregations: TeamAggregations
): number =>
  Math.max(
    0,
    (aggregations.memberCount ?? 0) -
      (aggregations.managerCount ?? 0) -
      (aggregations.unassignedCount ?? 0)
  );

export type LayersMetricResult = {
  current: number | null;
  target: number;
  delta: number | null;
  deltaFormatted: string;
  ragStatus: RagStatus;
};

export type LayersMetricOptions = {
  hierarchyView?: string | null | undefined;
  teamDepthFromTop?: number | undefined;
};

export const calculateLayersMetric = (
  aggregations: TeamAggregations,
  scenario = "standard",
  targetsByScenario?: ScenarioTargetOverrides,
  options: LayersMetricOptions = {}
): LayersMetricResult => {
  const { hierarchyView, teamDepthFromTop = 0 } = options;
  // reporting_line depths are measured from the org root (HRIS climb), so the
  // viewed team's own depth must be subtracted; supply depths are already
  // team-relative.
  const isReportingLine = isReportingLineHierarchySource(hierarchyView);
  const target = Math.max(
    1,
    getScenarioTargets(scenario, targetsByScenario).layers.max -
      (isReportingLine ? teamDepthFromTop : 0)
  );
  const current =
    aggregations.managementLayersMax != null
      ? Math.max(
          0,
          aggregations.managementLayersMax -
            (isReportingLine ? teamDepthFromTop : 0)
        )
      : null;

  if (current === null) {
    return {
      current: null,
      target,
      delta: null,
      deltaFormatted: "N/A",
      ragStatus: "grey",
    };
  }

  const delta = current - target;
  return {
    current,
    target,
    delta,
    deltaFormatted: delta === 0 ? "0" : delta > 0 ? `+${delta}` : `${delta}`,
    ragStatus: getRAGStatus(current, target, "layers"),
  };
};

export type SpanMetricResult = {
  current: number | null;
  target: SpanRange;
  targetFormatted: string;
  delta: number | null;
  deltaFormatted: string;
  ragStatus: RagStatus;
};

export const calculateSpanMetric = (
  aggregations: TeamAggregations,
  scenario = "standard",
  targetsByScenario?: ScenarioTargetOverrides
): SpanMetricResult => {
  const { min, max } = getScenarioTargets(scenario, targetsByScenario).span;
  const target = { min, max };
  const targetFormatted = `${min}-${max}`;

  if (aggregations.directReportsAvg == null) {
    return {
      current: null,
      target,
      targetFormatted,
      delta: null,
      deltaFormatted: "N/A",
      ragStatus: "grey",
    };
  }

  const current = round(aggregations.directReportsAvg);
  let delta = 0;
  if (current < min) delta = current - min;
  else if (current > max) delta = current - max;

  return {
    current,
    target,
    targetFormatted,
    delta: round(delta),
    deltaFormatted: formatSignedDecimal2(round(delta)),
    ragStatus: getRAGStatus(current, target, "span"),
  };
};

export type ManagerToICRatioMetricResult = {
  current: number | null;
  currentFormatted: string;
  target: number;
  targetFormatted: string;
  delta: number | null;
  deltaFormatted: string;
  ragStatus: RagStatus;
  /** null while population is unknown (partial/empty aggregations payload). */
  managers: number | null;
  ics: number | null;
  unassigned: number | null;
};

export const calculateManagerToICRatioMetric = (
  aggregations: TeamAggregations,
  scenario = "standard",
  targetsByScenario?: ScenarioTargetOverrides
): ManagerToICRatioMetricResult => {
  const targetRatioMin = getScenarioTargets(scenario, targetsByScenario)
    .managerToICRatio.min;
  const targetFormatted = `1:${Math.round(1 / targetRatioMin)}`;

  // Unknown population — don't fabricate 0 managers/ICs (that skips loading UI).
  if (aggregations.memberCount == null) {
    return {
      current: null,
      currentFormatted: "N/A",
      target: targetRatioMin,
      targetFormatted,
      delta: null,
      deltaFormatted: "N/A",
      ragStatus: "grey",
      managers: null,
      ics: null,
      unassigned: null,
    };
  }

  const managers = aggregations.managerCount ?? 0;
  const ics = calculateIndividualContributorCount(aggregations);
  const unassigned = aggregations.unassignedCount ?? 0;

  // Ratio is undefined (not merely low) when either side is 0 — no managers
  // to divide by, or no ICs to have a ratio at all. Report N/A/grey rather
  // than a fabricated current=0 driving a red/amber RAG status.
  if (aggregations.memberCount === 0 || managers === 0 || ics === 0) {
    return {
      current: null,
      currentFormatted: "N/A",
      target: targetRatioMin,
      targetFormatted,
      delta: null,
      deltaFormatted: "N/A",
      ragStatus: "grey",
      managers,
      ics,
      unassigned,
    };
  }

  const current = round(ics / managers);
  const delta = round(current - 1 / targetRatioMin);

  return {
    current,
    currentFormatted: formatRatio2(current),
    target: targetRatioMin,
    targetFormatted,
    delta,
    deltaFormatted: formatSignedDecimal2(delta),
    ragStatus: getRAGStatus(current, 1 / targetRatioMin, "ratio"),
    managers,
    ics,
    unassigned,
  };
};

export type ManagementOverheadMetricResult = {
  current: number | null;
  currentFormatted: string;
  target: number;
  targetFormatted: string;
  delta: number | null;
  deltaFormatted: string;
  ragStatus: RagStatus;
  managers: number;
  total: number;
  unassigned: number;
};

export const calculateManagementOverheadMetric = (
  aggregations: TeamAggregations,
  scenario = "standard",
  targetsByScenario?: ScenarioTargetOverrides
): ManagementOverheadMetricResult => {
  const target =
    getScenarioTargets(scenario, targetsByScenario).managementOverhead.max ??
    DEFAULT_VALUES.MANAGEMENT_OVERHEAD_MAX;
  const targetFormatted = `${target}%`;
  const total = calculateDesignCapacityHeadcount(aggregations) ?? 0;
  const managers = aggregations.managerCount ?? 0;
  const unassigned = aggregations.unassignedCount ?? 0;

  if (total === 0) {
    return {
      current: null,
      currentFormatted: "N/A",
      target,
      targetFormatted,
      delta: null,
      deltaFormatted: "N/A",
      ragStatus: "grey",
      managers: 0,
      total: 0,
      unassigned,
    };
  }

  const overhead = round((managers / total) * 100);
  const delta = round(overhead - target);

  return {
    current: overhead,
    currentFormatted: formatPercent2(overhead),
    target,
    targetFormatted,
    delta,
    deltaFormatted: formatSignedDecimal2(delta),
    ragStatus: getRAGStatus(overhead, target, "overhead"),
    managers,
    total,
    unassigned,
  };
};

export type HeadcountReductionMetricResult = {
  current: number | null;
  currentFormatted: string;
  target: number;
  targetFormatted: string;
  reductionNeeded: number | null;
  reductionNeededFormatted: string;
  delta: number | null;
  deltaFormatted: string;
  ragStatus: RagStatus;
  reductionPercentage: number;
};

export const EMPTY_HEADCOUNT_REDUCTION_METRIC: HeadcountReductionMetricResult =
  {
    current: null,
    currentFormatted: "N/A",
    target: 0,
    targetFormatted: "N/A",
    reductionNeeded: null,
    reductionNeededFormatted: "N/A",
    delta: null,
    deltaFormatted: "N/A",
    ragStatus: "grey",
    reductionPercentage: 0,
  };

export const calculateHeadcountReductionMetric = (
  aggregations: TeamAggregations,
  scenario = "standard",
  targetsByScenario?: ScenarioTargetOverrides
): HeadcountReductionMetricResult => {
  const reductionPercentage = getScenarioTargets(scenario, targetsByScenario)
    .headcountReduction.percentage;
  const reductionPercentageFormatted = round(reductionPercentage * 100, 1);

  const total = calculateDesignCapacityHeadcount(aggregations);
  if (total == null) {
    return EMPTY_HEADCOUNT_REDUCTION_METRIC;
  }

  if (total === 0) {
    return {
      ...EMPTY_HEADCOUNT_REDUCTION_METRIC,
      current: 0,
      currentFormatted: "0",
      reductionNeeded: 0,
      reductionNeededFormatted: "0",
      delta: 0,
      deltaFormatted: "0",
      reductionPercentage: reductionPercentageFormatted,
    };
  }

  const target = Math.round(total * (1 - reductionPercentage));
  const reductionNeeded = total - target;

  return {
    current: total,
    currentFormatted: total.toLocaleString(),
    target,
    targetFormatted: target.toLocaleString(),
    reductionNeeded,
    reductionNeededFormatted:
      reductionNeeded > 0 ? `-${reductionNeeded.toLocaleString()}` : "0",
    delta: reductionNeeded,
    deltaFormatted:
      reductionNeeded === 0 ? "0" : `+${reductionNeeded.toLocaleString()}`,
    ragStatus: getRAGStatus(total, target, "headcount"),
    reductionPercentage: reductionPercentageFormatted,
  };
};
